import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { User, LogOut, LayoutDashboard, Settings, Handshake } from "lucide-react";
import { useContext } from "react";
import { AuthContext } from "@/App";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotificationDropdown from "./NotificationDropdown";
import RewardsDropdown from "./RewardsDropdown";

// ReBusi Logo Icon Component
const ReBusiIcon = ({ className }) => (
  <svg viewBox="0 0 48 48" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M24 8C15.163 8 8 15.163 8 24s7.163 16 16 16" 
      stroke="#F7931E" 
      strokeWidth="3" 
      strokeLinecap="round"
      fill="none"
    />
    <path 
      d="M28 16l8 8-8 8" 
      stroke="#F7931E" 
      strokeWidth="3" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

export default function Navbar() {
  const { user, logout } = useContext(AuthContext);

  return (
    <>
      {/* Indian Tricolor Line */}
      <div className="tricolor-line fixed top-0 left-0 right-0 z-50"></div>
      
      <nav className="bg-slate-900 border-b border-slate-800 fixed top-1 left-0 right-0 z-50" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <ReBusiIcon className="w-9 h-9" />
              <span className="text-xl font-bold">
                <span className="text-orange-500">Re</span>
                <span className="text-white">Busi</span>
                <span className="text-orange-500">.</span>
              </span>
            </Link>

            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-300 hover:text-orange-400 font-medium transition-colors" data-testid="home-link">
                Home
              </Link>
              <Link to="/marketplace" className="text-slate-300 hover:text-orange-400 font-medium transition-colors" data-testid="marketplace-link">
                Marketplace
              </Link>
            <Link to="/consultants" className="text-slate-300 hover:text-orange-400 font-medium transition-colors" data-testid="consultants-link">
              Consultants
            </Link>
            <Link to="/valuation" className="text-slate-300 hover:text-orange-400 font-medium transition-colors" data-testid="valuation-link">
              Valuation
            </Link>
            <Link to="/franchises" className="text-slate-300 hover:text-orange-400 font-medium transition-colors" data-testid="franchises-link">
              Franchises
            </Link>
            <Link to="/rentals" className="text-slate-300 hover:text-orange-400 font-medium transition-colors" data-testid="rentals-link">
              Rentals
            </Link>
            
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" className="text-slate-300 hover:text-orange-400 font-medium transition-colors" data-testid="admin-link">
                    Admin
                  </Link>
                )}
                
                {/* User Actions - Notification, Rewards, Profile (grouped closely) */}
                <div className="flex items-center gap-1">
                  {/* Notification Bell */}
                  <NotificationDropdown />
                  
                  {/* Rewards/Gift Icon */}
                  <RewardsDropdown />
                  
                  <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2 border-slate-600 text-white hover:bg-slate-800" data-testid="user-menu-button">
                      <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold">
                        {user.name?.charAt(0)?.toUpperCase() || "U"}
                      </div>
                      {user.name?.split(" ")[0]}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="cursor-pointer" data-testid="dropdown-dashboard">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/deals" className="cursor-pointer" data-testid="dropdown-deals">
                        <Handshake className="w-4 h-4 mr-2" />
                        My Deals
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="cursor-pointer" data-testid="dropdown-profile">
                        <User className="w-4 h-4 mr-2" />
                        My Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600" data-testid="dropdown-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                </div>
              </>
            ) : (
              <Link to="/auth">
                <Button className="bg-orange-500 hover:bg-orange-600" size="sm" data-testid="signin-button">
                  Sign In
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
    </>
  );
}